import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { ArrowLeft, ChevronDown, ChevronUp, Maximize2, MessagesSquare, Pencil, Swords, Users, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@artstorefronts/ui';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '~/context/analytics';
import { useMessagingDock } from '~/context/messagingDock';
import {
  DOCK_PREVIEW_MESSAGES_VARIABLES,
  MESSAGES_QUERY,
  MESSAGE_QUERY,
  MY_PROFILE_QUERY,
  SOCIAL_BADGE_COUNTS_QUERY,
} from '~/graphql/queries';
import { UserAvatar } from '~/components/UserAvatar';
import { DisabledUserName, getDisabledUserName } from '~/components/DisabledUserName';
import { buildTownSquareUrl, getPublicSiteBaseUrl } from '~/utils/index';
import { stashComposeDraft } from '~/utils/composeDraftHandoff';
import { INBOX_MESSAGES_REFETCH_WRITE_POLICY } from '@arthelper/shared/inbox-messages-cache';
import { DockListView } from './DockListView';
import { DockThreadView } from './DockThreadView';
import { DockComposeView } from './DockComposeView';
import { ThreadActionsMenu, type ThreadParticipant } from './ThreadActionsMenu';

/**
 * LinkedIn-style multi-panel messaging dock.
 *
 * Desktop: the main dock (list / compose) is pinned to the bottom-right.
 * When a thread is open, it renders as a separate floating panel to the
 * LEFT of the main dock, each with its own minimize/expand state so a
 * user can park a thread without losing the inbox.
 *
 * Mobile: the same data is rendered single-panel — the thread panel
 * takes over when present, otherwise the main dock shows. Height
 * animates between collapsed (44px) and expanded on both form factors.
 */

const COLLAPSED_HEIGHT = 44;
const EXPANDED_HEIGHT = 560;
const MAIN_WIDTH = 380; // matches `md:w-[380px]` above; used to position chips

/** Right inset for unread chips: viewport padding (md:right-4) + gap from dock */
const CHIP_RIGHT_OFFSET_COLLAPSED_MAIN_PX = MAIN_WIDTH + 16 + 8;
const CHIP_RIGHT_OFFSET_NO_COLLAPSED_MAIN_PX = 16 + 8;

export function MessagingDock({
  /** On AI chat routes: hide the collapsed “Messaging” strip so it doesn’t cover messages; chips + thread panels still show. */
  suppressCollapsedMain = false,
}: {
  suppressCollapsedMain?: boolean;
} = {}) {
  const { threadPanel, mobileActivePanel, mainIsExpanded } = useMessagingDock();

  // Keep the inbox cache warm so unread chips + list view are populated
  // without requiring the user to open the dock first. Refetches must merge:
  // Apollo overwrites on refetch and would drop the paged tail.
  useQuery(MESSAGES_QUERY, {
    variables: DOCK_PREVIEW_MESSAGES_VARIABLES,
    fetchPolicy: 'cache-and-network',
    pollInterval: 60000,
    refetchWritePolicy: INBOX_MESSAGES_REFETCH_WRITE_POLICY,
  });

  const chipRightPx =
    suppressCollapsedMain && !mainIsExpanded
      ? CHIP_RIGHT_OFFSET_NO_COLLAPSED_MAIN_PX
      : CHIP_RIGHT_OFFSET_COLLAPSED_MAIN_PX;

  return (
    <>
      <UnreadThreadChips chipRightPx={chipRightPx} />
      <div className="fixed z-40 bottom-0 left-4 right-4 md:left-auto md:right-4 flex items-end justify-end gap-2 pointer-events-none">
        {threadPanel && (
          <ThreadPanelWindow
            threadId={threadPanel.threadId}
            isExpanded={threadPanel.isExpanded}
            mobileHidden={mobileActivePanel !== 'thread'}
          />
        )}
        <MainDockWindow mobileHidden={mobileActivePanel !== 'main'} suppressCollapsedMain={suppressCollapsedMain} />
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Main dock (list / compose)
// ─────────────────────────────────────────────────────────────────────────

function MainDockWindow({
  mobileHidden,
  suppressCollapsedMain,
}: {
  mobileHidden: boolean;
  suppressCollapsedMain?: boolean;
}) {
  const { mainFrame, mainIsExpanded } = useMessagingDock();

  if (suppressCollapsedMain && !mainIsExpanded) {
    return null;
  }

  return (
    <div
      role="dialog"
      aria-label="Messaging"
      aria-expanded={mainIsExpanded}
      className={`
        pointer-events-auto
        bg-white shadow-2xl border border-b-0 border-gray-200
        rounded-t-lg flex flex-col overflow-hidden
        transition-[height] duration-200 ease-out
        md:w-[380px] md:flex-shrink-0
        ${mobileHidden ? 'hidden md:flex' : 'flex w-full'}
      `}
      style={{
        height: mainIsExpanded ? `min(${EXPANDED_HEIGHT}px, calc(100vh - 1rem))` : `${COLLAPSED_HEIGHT}px`,
      }}
    >
      {mainIsExpanded ? <MainExpandedHeader /> : <MainCollapsedHeader />}
      <div className="flex-1 min-h-0 flex flex-col" aria-hidden={!mainIsExpanded}>
        {mainFrame.view === 'list' && <DockListView />}
        {mainFrame.view === 'compose' && (
          <DockComposeView
            key={`compose:${mainFrame.prefillRecipientAsfId ?? ''}:${mainFrame.prefillBody ?? ''}`}
            prefillRecipientAsfId={mainFrame.prefillRecipientAsfId}
            prefillRecipient={mainFrame.prefillRecipient}
            prefillBody={mainFrame.prefillBody}
          />
        )}
      </div>
    </div>
  );
}

function MainCollapsedHeader() {
  const { t } = useTranslation('social');
  const {
    amplitude: { track },
  } = useAnalytics();
  const { expandMain } = useMessagingDock();
  const { data } = useQuery(SOCIAL_BADGE_COUNTS_QUERY, { fetchPolicy: 'cache-only' });
  const unread: number = data?.unreadDmCount ?? 0;

  return (
    <button
      type="button"
      onClick={() => {
        track('dm_dock_main_expanded', { source: 'main_collapsed_bar' });
        expandMain();
      }}
      className="flex-shrink-0 w-full h-11 flex items-center justify-between px-3 bg-white hover:bg-gray-50 transition-colors rounded-t-lg text-left"
      aria-label={t('messages.dock.expand', 'Open messaging')}
    >
      <div className="flex items-center gap-2 min-w-0">
        <MessagesSquare className="size-5 text-gray-700 flex-shrink-0" />
        <span className="text-sm font-semibold text-gray-900 truncate">
          {t('messages.dock.list-title', 'Messaging')}
        </span>
        {unread > 0 && (
          <span className="bg-red-500 text-white text-xs font-semibold rounded-full px-2 py-0.5 min-w-[20px] text-center flex-shrink-0">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </div>
      <ChevronUp className="size-4 text-gray-500 flex-shrink-0" />
    </button>
  );
}

function MainExpandedHeader() {
  const { t } = useTranslation('social');
  const {
    amplitude: { track },
  } = useAnalytics();
  const { mainFrame, minimizeMain, openCompose, backFromCompose, getComposeDraft } = useMessagingDock();

  const isCompose = mainFrame.view === 'compose';
  const openInFullPageHref = (() => {
    if (!isCompose) return '/messages';
    const params = new URLSearchParams();
    if (mainFrame.prefillRecipientAsfId) params.set('to', mainFrame.prefillRecipientAsfId);
    const name =
      mainFrame.prefillRecipient?.displayName ||
      [mainFrame.prefillRecipient?.firstName, mainFrame.prefillRecipient?.lastName].filter(Boolean).join(' ');
    if (name) params.set('name', name);
    // Draft body stays out of the URL (sessionStorage handoff on click).
    const qs = params.toString();
    return qs ? `/messages/new?${qs}` : '/messages/new';
  })();

  return (
    <div className="flex-shrink-0 w-full h-11 flex items-center justify-between border-b border-gray-200 px-3 bg-white rounded-t-lg">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {isCompose ? (
          <button
            type="button"
            onClick={backFromCompose}
            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
            aria-label={t('messages.dock.back', 'Back')}
          >
            <ArrowLeft className="size-4 text-gray-700" />
          </button>
        ) : (
          <MessagesSquare className="size-5 text-gray-700 ml-1 flex-shrink-0" />
        )}
        <h2 className="text-sm font-semibold text-gray-900 truncate">
          {isCompose ? t('messages.dock.compose-title', 'New message') : t('messages.dock.list-title', 'Messaging')}
        </h2>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <Link
          to={openInFullPageHref}
          onClick={() => {
            if (isCompose) {
              const draft = getComposeDraft();
              if (draft) stashComposeDraft(draft);
            }
            track('dm_dock_main_minimized', { source: 'main_expanded_open_full_page' });
            minimizeMain();
          }}
          className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
          aria-label={t('messages.dock.open-full-page', 'Open in full page')}
          title={t('messages.dock.open-full-page', 'Open in full page')}
        >
          <Maximize2 className="size-4 text-gray-600" />
        </Link>
        {!isCompose && (
          <button
            type="button"
            onClick={() => {
              openCompose({ source: 'main_expanded_header' });
            }}
            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
            aria-label={t('messages.dock.new-message', 'New message')}
            title={t('messages.dock.new-message', 'New message')}
          >
            <Pencil className="size-4 text-gray-600" />
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            track('dm_dock_main_minimized', { source: 'main_expanded_header' });
            minimizeMain();
          }}
          className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
          aria-label={t('messages.dock.minimize', 'Minimize')}
          title={t('messages.dock.minimize', 'Minimize')}
        >
          <ChevronDown className="size-4 text-gray-600" />
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Thread panel (separate window to the LEFT of the main dock on desktop)
// ─────────────────────────────────────────────────────────────────────────

function ThreadPanelWindow({
  threadId,
  isExpanded,
  mobileHidden,
}: {
  threadId: string;
  isExpanded: boolean;
  mobileHidden: boolean;
}) {
  return (
    <div
      role="dialog"
      aria-label="Conversation"
      aria-expanded={isExpanded}
      className={`
        pointer-events-auto
        bg-white shadow-2xl border border-b-0 border-gray-200
        rounded-t-lg flex flex-col overflow-hidden
        transition-[height] duration-200 ease-out
        md:w-[360px] md:flex-shrink-0
        ${mobileHidden ? 'hidden md:flex' : 'flex w-full'}
      `}
      style={{
        height: isExpanded ? `min(${EXPANDED_HEIGHT}px, calc(100vh - 1rem))` : `${COLLAPSED_HEIGHT}px`,
      }}
    >
      {isExpanded ? <ThreadExpandedHeader threadId={threadId} /> : <ThreadCollapsedHeader threadId={threadId} />}
      <div className="flex-1 min-h-0 flex flex-col" aria-hidden={!isExpanded}>
        <DockThreadView threadId={threadId} isVisible={isExpanded} />
      </div>
    </div>
  );
}

function ThreadCollapsedHeader({ threadId }: { threadId: string }) {
  const { t } = useTranslation('social');
  const {
    amplitude: { track },
  } = useAnalytics();
  const { expandThread, closeThread } = useMessagingDock();
  const { label, others, unread } = useThreadHeaderData(threadId);

  return (
    <div className="flex-shrink-0 w-full h-11 flex items-center gap-2 pl-3 pr-1 bg-white rounded-t-lg">
      <button
        type="button"
        onClick={() => {
          expandThread({ source: 'thread_collapsed_bar' });
        }}
        className="flex-1 min-w-0 h-full flex items-center gap-2 hover:bg-gray-50 -mx-3 px-3 transition-colors rounded-t-lg text-left"
        aria-label={t('messages.dock.expand-thread', 'Expand conversation')}
      >
        <HeaderAvatar others={others} size={24} />
        <span className="text-sm font-semibold text-gray-900 truncate">{label}</span>
        {unread > 0 && (
          <span className="bg-red-500 text-white text-[10px] font-semibold rounded-full px-1.5 py-0.5 min-w-[18px] text-center flex-shrink-0">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      <button
        type="button"
        onClick={() => {
          track('dm_dock_thread_closed', { thread_id: threadId, source: 'thread_collapsed_bar' });
          closeThread();
        }}
        className="p-1.5 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
        aria-label={t('messages.dock.close-thread', 'Close conversation')}
      >
        <X className="size-4 text-gray-500" />
      </button>
    </div>
  );
}

function ThreadExpandedHeader({ threadId }: { threadId: string }) {
  const { t } = useTranslation('social');
  const {
    amplitude: { track },
  } = useAnalytics();
  const { minimizeThread, closeThread } = useMessagingDock();
  const { label, others, guild } = useThreadHeaderData(threadId);
  const isGroup = others.length > 1;

  // Build a participant list for the actions menu. Only include in-app users
  // (contacts don't have block/report support).
  const actionParticipants: ThreadParticipant[] = others
    .filter((p) => p.user?.asfId)
    .map((p) => ({
      userId: String(p.user!.asfId),
      profileId: p.user!.profile?.id ?? undefined,
      displayName:
        p.user!.profile?.displayName ||
        [p.user!.firstName, p.user!.lastName].filter(Boolean).join(' ') ||
        p.user!.profile?.slug ||
        'Unknown',
    }));

  // 1:1 only: link the name to the other person's public town-square
  // profile (mirrors the ParticipantListPopover slug/disabled guard).
  // Uses buildTownSquareUrl so the viewer's preferred locale prefix survives
  // the cross-app hop — a raw base URL would drop non-`en` users on the
  // English profile (or into a GeoIP-based middleware redirect).
  const otherUser = !isGroup ? others[0]?.user : null;
  const otherProfileSlug = otherUser && !otherUser.profile?.isDisabled ? (otherUser.profile?.slug ?? null) : null;

  const titleContent = (
    <>
      <HeaderAvatar others={others} size={28} />
      <h2 className="text-sm font-semibold text-gray-900 truncate" title={label}>
        {otherProfileSlug && otherUser ? (
          <a
            href={buildTownSquareUrl(`/${otherProfileSlug}`)}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2"
            onClick={() => {
              track('dm_thread_header_profile_link_clicked', {
                thread_id: threadId,
                profile_user_id: String(otherUser.asfId),
                source: 'thread_header',
              });
            }}
          >
            {label}
          </a>
        ) : (
          label
        )}
      </h2>
      {isGroup && <Users className="size-3.5 text-gray-400 flex-shrink-0" aria-hidden="true" />}
    </>
  );

  return (
    <div className="flex-shrink-0 w-full h-11 flex items-center justify-between border-b border-gray-200 px-3 bg-white rounded-t-lg">
      {isGroup ? (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 min-w-0 flex-1 h-full -mx-3 px-3 hover:bg-gray-50 transition-colors text-left"
              aria-label={t('messages.dock.view-participants', 'View {{count}} participants', {
                count: others.length,
              })}
            >
              {titleContent}
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-72 p-0">
            <ParticipantListPopover others={others} />
          </PopoverContent>
        </Popover>
      ) : (
        <div className="flex items-center gap-2 min-w-0 flex-1">{titleContent}</div>
      )}
      <div className="flex items-center gap-1 flex-shrink-0">
        {/* A guild room's way home: the guild page on town-square, landing on its Chat tab. */}
        {guild && (
          <a
            href={buildTownSquareUrl(`/guilds/${guild.slug}?tab=chat`)}
            onClick={() => {
              track('dm_thread_header_guild_link_clicked', {
                thread_id: threadId,
                guild_id: String(guild.id),
                source: 'thread_header',
              });
            }}
            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
            aria-label={t('messages.dock.open-guild', 'Open {{name}}', { name: guild.name })}
            title={t('messages.dock.open-guild', 'Open {{name}}', { name: guild.name })}
          >
            <Swords className="size-4 text-gray-600" />
          </a>
        )}
        {actionParticipants.length > 0 && (
          <ThreadActionsMenu
            messageId={threadId}
            participants={actionParticipants}
            onBlock={(userId) => {
              track('dm_user_blocked', { thread_id: threadId, user_id: userId, source: 'thread_header' });
              if (!isGroup) {
                closeThread();
              }
            }}
            onLeave={() => closeThread()}
          />
        )}
        <Link
          to={`/messages/${threadId}`}
          onClick={() => {
            track('dm_dock_thread_minimized', { thread_id: threadId, source: 'thread_expanded_open_full_page' });
            minimizeThread();
          }}
          className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
          aria-label={t('messages.dock.open-full-page', 'Open in full page')}
          title={t('messages.dock.open-full-page', 'Open in full page')}
        >
          <Maximize2 className="size-4 text-gray-600" />
        </Link>
        <button
          type="button"
          onClick={() => {
            track('dm_dock_thread_minimized', { thread_id: threadId, source: 'thread_expanded_header' });
            minimizeThread();
          }}
          className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
          aria-label={t('messages.dock.minimize', 'Minimize')}
          title={t('messages.dock.minimize', 'Minimize')}
        >
          <ChevronDown className="size-4 text-gray-600" />
        </button>
        <button
          type="button"
          onClick={() => {
            track('dm_dock_thread_closed', { thread_id: threadId, source: 'thread_expanded_header' });
            closeThread();
          }}
          className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
          aria-label={t('messages.dock.close-thread', 'Close conversation')}
          title={t('messages.dock.close-thread', 'Close conversation')}
        >
          <X className="size-4 text-gray-600" />
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Shared header helpers (name + avatar(s) for 1:1 and group threads)
// ─────────────────────────────────────────────────────────────────────────

export interface OtherPart {
  user?: {
    asfId: string;
    firstName?: string | null;
    lastName?: string | null;
    profile?: {
      id?: string | null;
      slug?: string | null;
      avatarUrl?: string | null;
      displayName?: string | null;
      isHumanVerified?: boolean | null;
      isDisabled?: boolean | null;
    } | null;
  } | null;
  contact?: { id: string; name?: string | null } | null;
}

export function getOtherParticipants(message: any, viewerAsfId: string | undefined): OtherPart[] {
  const isInApp = message?.channel?.toLowerCase() === 'in_app';
  const list: OtherPart[] = message?.participants ?? [];
  if (!isInApp) return list.filter((p) => p.contact);
  if (!viewerAsfId) return list.filter((p) => p.user);
  return list.filter((p) => p.user && p.user.asfId?.toString() !== viewerAsfId);
}

export function participantFullName(p: OtherPart): string {
  const u = p.user;
  if (u) {
    if (u.profile?.isDisabled) return getDisabledUserName();
    return (
      u.profile?.displayName || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.profile?.slug || 'Unknown'
    );
  }
  return p.contact?.name || 'Unknown';
}

function participantFirstName(p: OtherPart): string {
  const u = p.user;
  if (u?.firstName) return u.firstName;
  if (u?.profile?.displayName) return u.profile.displayName.split(/\s+/)[0] ?? u.profile.displayName;
  return participantFullName(p).split(/\s+/)[0] ?? 'Unknown';
}

export function formatParticipantsLabel(others: OtherPart[]): string {
  if (others.length === 0) return 'Unknown';
  if (others.length === 1) return participantFullName(others[0]);
  const firsts = others.map(participantFirstName);
  if (firsts.length === 2) return `${firsts[0]}, ${firsts[1]}`;
  return `${firsts[0]}, ${firsts[1]} +${firsts.length - 2}`;
}

function useThreadHeaderData(threadId: string) {
  const { data } = useQuery(MESSAGE_QUERY, {
    variables: { id: threadId },
    fetchPolicy: 'cache-first',
  });
  const myProfile = useQuery(MY_PROFILE_QUERY, { fetchPolicy: 'cache-first' });
  const viewerAsfId: string | undefined = myProfile.data?.profile?.user?.asfId?.toString();

  const message = data?.message;
  const others = message ? getOtherParticipants(message, viewerAsfId) : [];
  // A guild room is the guild, by name — same rule as DockListView.
  const guild: { id: string; slug: string; name: string } | null = message?.guild ?? null;
  const label = message ? (guild?.name ?? formatParticipantsLabel(others)) : 'Message';
  const unread: number = message?.unreadCount ?? 0;
  return { message, others, label, unread, guild };
}

function HeaderAvatar({ others, size }: { others: OtherPart[]; size: number }) {
  if (others.length === 0) {
    return (
      <div
        className="flex-shrink-0 rounded-full bg-gray-200 flex items-center justify-center"
        style={{ width: size, height: size }}
      >
        <MessagesSquare className="size-3 text-gray-500" />
      </div>
    );
  }
  if (others.length === 1) {
    const p = others[0];
    return (
      <div className="flex-shrink-0">
        <UserAvatar
          avatarUrl={p.user?.profile?.avatarUrl ?? null}
          firstName={p.user?.firstName}
          lastName={p.user?.lastName}
          displayName={p.user?.profile?.displayName || p.contact?.name}
          size={size}
          isHumanVerified={!!p.user?.profile?.isHumanVerified}
          isDisabled={!!p.user?.profile?.isDisabled}
        />
      </div>
    );
  }
  return <ParticipantAvatarStack others={others} size={size} />;
}

export function ParticipantAvatarStack({ others, size = 24 }: { others: OtherPart[]; size?: number }) {
  const slice = others.slice(0, 3);
  const overlap = Math.round(size * 0.55);
  return (
    <div
      className="relative flex-shrink-0 flex items-center"
      style={{ width: size + (slice.length - 1) * overlap, height: size }}
    >
      {slice.map((p, i) => (
        <div
          key={(p.user?.asfId ?? p.contact?.id ?? i) + ''}
          className="absolute rounded-full ring-2 ring-white"
          style={{ left: i * overlap, zIndex: slice.length - i }}
        >
          <UserAvatar
            avatarUrl={p.user?.profile?.avatarUrl ?? null}
            firstName={p.user?.firstName}
            lastName={p.user?.lastName}
            displayName={p.user?.profile?.displayName || p.contact?.name}
            size={size}
          />
        </div>
      ))}
    </div>
  );
}

function ParticipantListPopover({ others }: { others: OtherPart[] }) {
  const { t } = useTranslation('social');
  return (
    <div className="flex flex-col">
      <div className="px-3 py-2 border-b border-gray-100 text-xs font-semibold uppercase tracking-wide text-gray-500">
        {t('messages.dock.participants-heading', 'In this conversation ({{count}})', {
          count: others.length,
        })}
      </div>
      <ul className="max-h-64 overflow-y-auto py-1">
        {others.map((p, i) => {
          const isDisabled = !!p.user?.profile?.isDisabled;
          const name = participantFullName(p);
          const slug = !isDisabled ? p.user?.profile?.slug : null;
          const body = (
            <div className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50">
              <UserAvatar
                avatarUrl={p.user?.profile?.avatarUrl ?? null}
                firstName={p.user?.firstName}
                lastName={p.user?.lastName}
                displayName={p.user?.profile?.displayName || p.contact?.name}
                size={32}
                isDisabled={isDisabled}
              />
              <span className="text-sm text-gray-900 truncate">{isDisabled ? <DisabledUserName /> : name}</span>
            </div>
          );
          const key = (p.user?.asfId ?? p.contact?.id ?? i) + '';
          // Public profiles live on the marketing/town-square site, not the
          // client app — open in a new tab so the user doesn't lose the dock.
          return (
            <li key={key}>
              {slug ? (
                <a
                  href={`${getPublicSiteBaseUrl()}/${slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  {body}
                </a>
              ) : (
                body
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Unread thread chips (desktop only, floating to the left of the dock)
// ─────────────────────────────────────────────────────────────────────────

function UnreadThreadChips({ chipRightPx }: { chipRightPx: number }) {
  const { mainIsExpanded, threadPanel, openThread } = useMessagingDock();
  const myProfile = useQuery(MY_PROFILE_QUERY, { fetchPolicy: 'cache-first' });
  const viewerAsfId: string | undefined = myProfile.data?.profile?.user?.asfId?.toString();

  // cache-only still network-refetches when a reply includes this query, and that
  // write shares the inbox cache entry with the list.
  const { data } = useQuery(MESSAGES_QUERY, {
    variables: DOCK_PREVIEW_MESSAGES_VARIABLES,
    fetchPolicy: 'cache-only',
    refetchWritePolicy: INBOX_MESSAGES_REFETCH_WRITE_POLICY,
  });

  // Hide chips when either a thread panel is open (user already has
  // quick access) or the main dock is expanded showing the full list.
  if (threadPanel || mainIsExpanded) return null;

  const items: any[] = data?.messages?.items ?? [];
  const unread = items
    .filter((m) => m.unreadCount > 0 && m.channel?.toLowerCase() === 'in_app')
    .sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime())
    .slice(0, 2);

  if (unread.length === 0) return null;

  return (
    <div
      className="pointer-events-none fixed bottom-0 z-40 hidden h-fit w-max items-end gap-2 md:flex"
      style={{ right: chipRightPx }}
    >
      {unread.map((m) => {
        const others = getOtherParticipants(m, viewerAsfId);
        const label = formatParticipantsLabel(others);
        return (
          <button
            key={m.id}
            type="button"
            onClick={() => {
              openThread(m.id, { source: 'unread_chip' });
            }}
            className="pointer-events-auto bg-white border border-b-0 border-gray-200 rounded-t-lg shadow-lg px-3 py-2 flex items-center gap-2 hover:bg-gray-50 transition-colors max-w-[260px]"
            aria-label={`Open conversation with ${label}`}
          >
            <div className="relative flex-shrink-0">
              <HeaderAvatar others={others} size={28} />
              <span className="absolute -top-1 -right-1 z-10 size-4 bg-red-500 rounded-full border-2 border-white text-[10px] font-bold text-white flex items-center justify-center">
                {m.unreadCount > 9 ? '9+' : m.unreadCount}
              </span>
            </div>
            <div className="min-w-0 text-left">
              <p className="text-xs font-semibold text-gray-900 truncate">{label}</p>
              {m.lastMessagePart?.content && (
                <p className="text-[11px] text-gray-500 truncate">{m.lastMessagePart.content}</p>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
